/**
 * A base class for creating custom errors that correctly extend the native Error class.
 * It ensures the error name is set to the class name, the prototype chain is correct
 * for `instanceof` checks, and provides cleaner stack traces in V8 environments.
 * This is the result of combining all the good stuff from:
 * https://stackoverflow.com/a/32749533/937477
 */
export class ExtendableError extends Error {
    constructor(message?: string) {
        // 1. Call the native Error constructor.
        // This initializes `this` as an Error object and sets the `message` property.
        // It also typically creates an initial `stack` property.
        super(message);

        // 2. Explicitly set the prototype of the instance to the prototype of the
        //    class that was actually constructed (e.g., `MySubError.prototype` if
        //    `new MySubError()` was called). `new.target` refers to this directly
        //    invoked constructor. This ensures `instanceof YourCustomError` works reliably
        //    across different JavaScript environments and transpilation targets.
        Object.setPrototypeOf(this, new.target.prototype);

        // 3. Set the error name to the name of the actual class being instantiated.
        //    `new.target.name` directly gives the name of the constructor function
        //    (e.g., "NotImplementedError").
        this.name = new.target.name;

        // 4. Enhance stack trace in V8 environments (Node.js, Chrome, etc.).
        //    This call to `Error.captureStackTrace` will modify `this.stack` to
        //    omit frames from the constructor (`new.target`) upwards, leading to
        //    a cleaner stack trace that points more directly to where the error
        //    was instantiated in the application code.
        //    If `Error.captureStackTrace` is not available (e.g., in Firefox, Safari),
        //    the original stack trace created by `super(message)` will be used.
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, new.target);
        }
    }
}

export class NotImplementedError extends ExtendableError {}

export class ExpectedError extends ExtendableError {}
